import { bffBaseUrl, bffOrigin } from "@/lib/bffBase";

/**
 * How the browser reaches the BFF: always straight to its origin, the base
 * URL `lib/bffBase.ts` reads (`NEXT_PUBLIC_API_BASE_URL`, baked by
 * `next.config.mjs`). There is no same-origin `/api` rewrite and no fallback:
 * a direct call that fails is returned as it failed.
 *
 * Why direct: a same-origin rewrite ran every call through the SSR Lambda's
 * middleware, so browser p75 was 1.2–2.4s against a BFF p50 of ~120ms. The
 * origin is the same for every tenant, so no per-tenant config is needed.
 *
 * Where each kind of build sends its calls (all to the one base URL):
 *  - deployed builds and local `bun run dev` on http://localhost:3000: the
 *    backend's BFF, which admits the page origin through its CORS list;
 *  - test builds (E2E, `dev:test`, Storybook, the unit suite): the
 *    unresolvable `.invalid` origin (`lib/bffTestOrigin.mjs`), answered by
 *    Playwright routes or MSW handlers and by nothing else;
 *  - mock mode (`bun run dev:mock`, the runtime toggle): the backend's BFF
 *    URL, answered by the MSW service worker before it leaves the page
 *    (`providers/MockModeProvider`). A worktree dev server on a port other
 *    than 3000 is off the BFF's CORS list, so it runs mock mode.
 *
 * The OTLP exporters (`lib/otel/tracer.ts`, `lib/otel/logger.ts`) post to the
 * BFF's `/diag/*` the same way: a cross-origin `keepalive` fetch with a CORS
 * preflight the BFF caches for 7200s.
 *
 * Rollback is revert and redeploy: there is no build flag that routes calls
 * anywhere else. A deployed origin problem is fixed on the BFF at runtime
 * through `CORS_ALLOWED_ORIGINS` (read at module load, so it needs a pod
 * restart), with no web deploy.
 * No preview deployments exist (only `uat` deployments and localhost), so no
 * preview origin needs admitting.
 *
 * Deploy note (the 2026-10-26 removal of the `/api` rewrite): the web deploy
 * is the only step; nothing changes on the BFF, whose CORS lists already
 * admit every deployed web host. Afterwards check that the deployed
 * Storybook bundle passes `scripts/check-storybook-origin.mjs`, the E2E suite
 * is green on its own port, the browser service's spans and logs appear in
 * Kibana for the day, the BFF's "origin not allowed, dropping" count for
 * `/diag/*` stays zero, and one smoke run against the UAT URL records a 202
 * for the ingest post. `CORS_ALLOWED_ORIGINS` is read at module load
 * (`telemetry.controller.ts`, `main.ts`), so changing it needs a pod restart.
 */

/**
 * Mirror of `DEFAULT_ALLOWED_ORIGINS` in `apps/bff/src/common/cors-origins.ts`,
 * same entry syntax: `*.suffix` matches the apex and any subdomain depth over
 * http/https; anything else is an exact origin. `bffTransport.test.ts` reads
 * the BFF file and fails when the two lists drift — change both together.
 *
 * Every page calls the BFF directly whatever its origin, so a page origin
 * missing here fails CORS on every call (`FETCH_ERROR`) and cannot boot; the
 * trace is the `direct-bff-unreachable` Sentry report
 * (`lib/directBffReachability.ts`). This list only decides what is warmed and
 * exported: the preconnect hint and the OTel exporters skip an origin the BFF
 * would refuse. The runtime list is `CORS_ALLOWED_ORIGINS` from
 * `apps/bff/.env`, which the BFF Dockerfile bakes into the image; the
 * deployment sets no CORS env for the BFF (checked 2026-10-19).
 *
 * So treat this list as part of every tenant-origin change. A tenant host
 * admitted on the BFF only through the runtime `CORS_ALLOWED_ORIGINS`, and
 * not listed here, makes its API calls normally but starts neither OTel SDK:
 * that host sends no browser spans or logs, and nothing reports the gap. Add
 * the host here (and to `DEFAULT_ALLOWED_ORIGINS`) in the same change.
 */
export const BFF_CORS_ALLOWED_ORIGINS: readonly string[] = [
  "*.endor-group.net",
  "*.universemoney.net",
  "*.universepayments.co.uk",
  "*.universemoney.io",
  "https://customers.app.orbis-exchange.com",
  "https://customers.app.moneylinkfx.com",
  // Local `bun run dev` on port 3000. Exact origins only: a worktree on
  // another port runs mock mode, and E2E and Storybook run on the test origin.
  "http://localhost:3000",
  "http://127.0.0.1:3000",
];

/**
 * Every non-safelisted request header the browser puts on a BFF call:
 * `authorization` and `content-type` from `baseQuery`'s `prepareHeaders`, and
 * the trace headers the OTel fetch instrumentation (`traceparent`,
 * `tracestate`) and Sentry (`sentry-trace`, `baggage`) inject on URLs matching
 * `bffPropagationTargets()`. A header missing from the BFF's
 * `CORS_ALLOWED_HEADERS` fails the preflight, and the call dies as an opaque
 * `FETCH_ERROR`. `bffTransport.test.ts` pins this list against the BFF's, and
 * `baseQuery.directTransport.test.ts` checks the real outgoing requests.
 *
 * There is no mock-mode marker: the MSW worker answers a mock-mode call
 * before it leaves the page, so nothing extra is ever sent.
 */
export const BFF_DIRECT_REQUEST_HEADERS: readonly string[] = [
  "authorization",
  "content-type",
  "traceparent",
  "tracestate",
  "baggage",
  "sentry-trace",
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Anchored, escaped pattern for URLs on the BFF origin. The trailing `/` stops
 * `https://api-customer.….net.evil.com/` and `…net:8443/` from matching.
 */
export function bffOriginPattern(): RegExp {
  return new RegExp(`^${escapeRegExp(bffOrigin())}/`);
}

/**
 * Extra targets for trace-header propagation (OTel
 * `propagateTraceHeaderCorsUrls`, Sentry `tracePropagationTargets`): the BFF
 * origin, which every BFF call now goes to.
 */
export function bffPropagationTargets(): RegExp[] {
  return [bffOriginPattern()];
}

function entryToMatcher(entry: string): (origin: string) => boolean {
  if (entry.startsWith("*.")) {
    // Same shape as the BFF's parseCorsOriginsCsv.
    const pattern = new RegExp(
      `^https?://([^/]+\\.)?${escapeRegExp(entry.slice(2))}$`,
      "i"
    );
    return (origin) => pattern.test(origin);
  }
  return (origin) => origin === entry;
}

const ALLOWED_ORIGIN_MATCHERS = BFF_CORS_ALLOWED_ORIGINS.map(entryToMatcher);

/** Whether the BFF's CORS list admits this page origin. */
export function isBffAllowedPageOrigin(origin: string): boolean {
  if (!origin || origin === "null") return false;
  return ALLOWED_ORIGIN_MATCHERS.some((matches) => matches(origin));
}

/** A `host` header naming the local machine, with or without a port. */
const LOCAL_DEV_HOST = /^(localhost|127\.0\.0\.1)(:\d+)?$/;

/**
 * The origin to `<link rel="preconnect">` from the root layout, or null.
 * Decided on the server, so the page's own host comes from the request's
 * `host` header: a host the BFF CORS list does not admit fails every call
 * anyway and must not pay a wasted TLS handshake. Deployed hosts are always
 * https; local dev (`localhost`, `127.0.0.1`) is http, so `localhost:3000` is
 * checked as `http://localhost:3000`.
 *
 * Skipped where no call reaches the network: a test build (its origin never
 * resolves) and a `dev:mock` build (the MSW worker answers every call).
 */
export function bffPreconnectOrigin(
  requestHost: string | null | undefined
): string | null {
  if (process.env.NEXT_PUBLIC_IS_TEST_ENV === "true") return null;
  if (process.env.NEXT_PUBLIC_MOCK_MODE === "true") return null;
  if (!requestHost) return null;
  const scheme = LOCAL_DEV_HOST.test(requestHost) ? "http" : "https";
  if (!isBffAllowedPageOrigin(`${scheme}://${requestHost}`)) return null;
  return bffOrigin();
}

/**
 * A URL on the BFF origin for one of its paths: `bffUrl("diag/ingest")`.
 * Resolved against the base, so a base path (if one is ever added) is kept.
 */
export function bffUrl(path: string): string {
  return new URL(path.replace(/^\//, ""), bffBaseUrl()).href;
}

// Same test as RTK's internal `isAbsoluteUrl`.
const ABSOLUTE_URL = /(^|:)\/\//;

/**
 * Join a base and a request URL exactly as `fetchBaseQuery`'s `baseUrl` did.
 * Absolute URLs (`extraOptions.external` calls) pass through untouched.
 */
export function joinBffUrl(base: string, url: string): string {
  if (!base) return url;
  if (!url) return base;
  if (ABSOLUTE_URL.test(url)) return url;
  const delimiter = base.endsWith("/") || !url.startsWith("?") ? "/" : "";
  return `${base.replace(/\/$/, "")}${delimiter}${url.replace(/^\//, "")}`;
}

// A path segment kept verbatim in telemetry: letters, `-` and `_` only.
const ROUTE_WORD = /^[a-z][a-z_-]*$/i;

/**
 * A BFF request path with every id-like segment replaced by `:id`, for
 * telemetry tags. Drops the query string and fragment; keeps only segments
 * made of letters, `-` and `_`, so UUIDs, numeric ids, IBANs, emails and
 * anything else carrying a digit, `@` or `.` never leave the browser.
 */
export function templatizeBffPath(url: string): string {
  const path = url.split(/[?#]/, 1)[0] ?? "";
  return path
    .split("/")
    .map((segment) =>
      segment === "" || ROUTE_WORD.test(segment) ? segment : ":id"
    )
    .join("/");
}
